import { inject, Injector, runInInjectionContext } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router, CanActivateFn, ActivatedRouteSnapshot } from '@angular/router';
import { firstValueFrom, filter, map } from 'rxjs';
import { AuthService } from '../services/auth.service';

/**
 * Wait for auth to finish loading, then evaluate with the given check function.
 * Returns true if check passes, otherwise navigates to the given fallback.
 */
function waitForAuth(
  authService: AuthService,
  injector: Injector,
  checkFn: () => boolean | import('@angular/router').UrlTree,
): Promise<boolean | import('@angular/router').UrlTree> | boolean | import('@angular/router').UrlTree {
  if (!authService.loading()) {
    return checkFn();
  }

  return runInInjectionContext(injector, () => {
    const loading$ = toObservable(authService.loading);
    return firstValueFrom(
      loading$.pipe(
        filter(loading => !loading),
        map(() => checkFn()),
      )
    );
  });
}

/**
 * Guard that requires user to be an admin
 */
export const adminGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const injector = inject(Injector);

  return waitForAuth(authService, injector, () => {
    if (authService.isAdmin()) {
      return true;
    }
    if (!authService.isAuthenticated()) {
      router.navigate(['/login']);
    } else {
      router.navigate(['/unauthorized']);
    }
    return false;
  });
};

/**
 * Guard that requires user to be a brand manager (or admin)
 */
export const brandManagerGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const injector = inject(Injector);

  return waitForAuth(authService, injector, () => {
    if (authService.isBrandManager()) {
      return true;
    }
    if (!authService.isAuthenticated()) {
      router.navigate(['/login']);
    } else {
      router.navigate(['/unauthorized']);
    }
    return false;
  });
};

/**
 * Guard that checks if user has access to a specific brand
 * Expects route to have :brandId parameter
 */
export const brandAccessGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const injector = inject(Injector);
  const brandId = route.paramMap.get('brandId');

  if (!brandId) {
    router.navigate(['/unauthorized']);
    return false;
  }

  return waitForAuth(authService, injector, () => {
    if (authService.hasBrandAccess(brandId)) {
      return true;
    }
    if (!authService.isAuthenticated()) {
      router.navigate(['/login']);
    } else {
      router.navigate(['/unauthorized']);
    }
    return false;
  });
};

/**
 * Guard for the /admin landing page. Any authenticated user with a
 * role can reach it — the page itself role-gates which cards render.
 * Unauthenticated visitors get bounced to /login.
 */
export const adminAreaGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const injector = inject(Injector);

  return waitForAuth(authService, injector, () => {
    if (!authService.isAuthenticated()) {
      router.navigate(['/login']);
      return false;
    }
    return true;
  });
};

/**
 * Guard for the manufacturer hub page (/admin/manufacturers/:mfgId/edit).
 * Admin can read any manufacturer; mfg_admin / mfg_viewer can read
 * only their own (mfg_viewer is read-only — write actions inside the
 * hub are gated by Firestore rules, not the route guard). Brand
 * roles are denied — they have no business reading the
 * mfg-name → plant_code mapping (privacy gate, see
 * docs/proposals/2026-05-04-manufacturer-access-model.md §5).
 */
export const manufacturerHubGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const injector = inject(Injector);
  const mfgId = route.paramMap.get('mfgId');

  if (!mfgId) {
    router.navigate(['/unauthorized']);
    return false;
  }

  return waitForAuth(authService, injector, () => {
    if (!authService.isAuthenticated()) {
      router.navigate(['/login']);
      return false;
    }
    if (authService.isAdmin()) return true;
    const profile = authService.userProfile();
    if (
      (profile?.role === 'mfg_admin' || profile?.role === 'mfg_viewer') &&
      profile.mfg_id === mfgId
    ) {
      return true;
    }
    router.navigate(['/unauthorized']);
    return false;
  });
};

/**
 * Guard that redirects authenticated users away from login page
 */
export const guestGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const injector = inject(Injector);

  return waitForAuth(authService, injector, () => {
    if (authService.isAuthenticated()) {
      router.navigate([authService.landingRouteForCurrentRole() || '/admin']);
      return false;
    }
    return true;
  });
};
